import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Lock, Sparkles, Check, Loader2, Clock, Gift } from "lucide-react";

const PERKS = {
    acquisizione: [
        "Mappa, poligoni, filtri e zone salvate",
        "Proprietari privati che vendono in autonomia nelle tue zone",
        "Match automatico con i tuoi acquirenti",
    ],
    nuovi_privati: [
        "Annunci dei privati sui portali in tempo reale",
        "Alert sui nuovi inserimenti nelle tue zone operative",
        "Contatto rapido e storico degli annunci visti",
    ],
    next_seller: [
        "Venditori in procinto di mettere casa sul mercato, prima degli altri",
        "Segnali raccolti ogni notte nelle tue zone operative con punteggio di affidabilità",
        "Collegamento diretto al CRM e ricerche salvate",
    ],
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "");
const fmtPrice = (n) => `${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: Number(n) % 1 ? 2 : 0 })}€`;

export default function AddonGate({ module = "acquisizione", children }) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = () => api.get("/agency/addons/status").then((r) => setState(r.data.modules?.[module] || null)).catch(() => setState({ unlocked: false, reason: "locked", price_monthly: 99, trial_days: 30, can_start_trial: false }));
    useEffect(() => { load(); }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

    const startTrial = async () => {
        setBusy(true); setError(null);
        try {
            const { data } = await api.post(`/agency/addons/${module}/start-trial`);
            setState(data.module);
        } catch (e) {
            setError(e?.response?.data?.detail || "Impossibile avviare la prova gratuita. Riprova.");
        } finally { setBusy(false); }
    };
    const checkout = async () => {
        setBusy(true); setError(null);
        try {
            const { data } = await api.post(`/agency/addons/${module}/checkout`);
            window.location.href = data.url;
        } catch (e) {
            setError(e?.response?.data?.detail || "Impossibile avviare il pagamento. Riprova.");
            setBusy(false);
        }
    };

    if (!state) {
        return <div data-testid="addon-gate-loading" className="flex items-center gap-2 py-20 justify-center text-sm text-ink-400"><Loader2 size={16} className="animate-spin" /> Verifica modulo…</div>;
    }

    if (state.unlocked) {
        return (
            <>
                {state.reason === "trial" && (
                    <div data-testid="addon-trial-banner" className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)] px-4 py-3 text-xs text-[var(--pd-text)]">
                        <Clock size={14} className="text-[var(--pd-accent)]" />
                        <span>Prova gratuita attiva fino al <b data-testid="addon-trial-ends">{fmtDate(state.trial_ends_at)}</b>.</span>
                        <span className="text-[var(--pd-muted)]">Dal giorno successivo il modulo costa <b className="text-[var(--pd-text)]">{fmtPrice(state.price_monthly)}/mese</b>: potrai attivarlo quando vuoi dalla dashboard, senza addebiti automatici.</span>
                    </div>
                )}
                {state.reason === "free" && (
                    <div data-testid="addon-free-banner" className="mb-5 flex items-center gap-2 rounded-2xl border border-[var(--pd-tag-green)]/30 bg-[var(--pd-tag-green-bg)] px-4 py-2.5 text-xs text-[var(--pd-tag-green)]"><Gift size={14} /> Modulo incluso gratuitamente per la tua agenzia.</div>
                )}
                {children}
            </>
        );
    }

    const price = state.price_monthly ?? 99;
    const trialExpired = state.reason === "trial_expired";
    const trialEnd = new Date(Date.now() + (state.trial_days || 30) * 86400000);
    return (
        <div data-testid="addon-gate-locked" className="mx-auto max-w-xl py-10">
            <div className="pd-card rounded-3xl p-8">
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[var(--pd-icon-bg)] text-[var(--pd-icon)]"><Lock size={24} /></span>
                <span className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[var(--pd-accent-bg)] px-3 py-1 text-[11px] font-semibold text-[var(--pd-accent)]"><Sparkles size={12} /> Add-on Agenzia Pro</span>
                <h3 className="mt-3 text-2xl font-semibold text-[var(--pd-text)]">{trialExpired ? `La prova gratuita di ${state.name} è terminata` : `Sblocca ${state.name}`}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--pd-muted)]">
                    {trialExpired
                        ? <>Il periodo di prova si è concluso il <b className="text-[var(--pd-text)]">{fmtDate(state.trial_ends_at)}</b>. Attiva l’abbonamento mensile per continuare a usare il modulo.</>
                        : <>Modulo dedicato al tuo piano Agenzia Pro. {state.can_start_trial && <>Provalo gratis per <b className="text-[var(--pd-text)]">{state.trial_days} giorni</b>: la prova termina il <b className="text-[var(--pd-text)]">{fmtDate(trialEnd.toISOString())}</b>, poi {fmtPrice(price)}/mese.</>}</>}
                </p>
                <ul className="mt-6 space-y-2.5">
                    {(PERKS[module] || []).map((p) => (
                        <li key={p} className="flex items-start gap-2.5 text-sm text-[var(--pd-text)]">
                            <Check size={16} className="mt-0.5 shrink-0 text-[var(--pd-icon)]" /> {p}
                        </li>
                    ))}
                </ul>
                <div className="mt-8 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-3xl font-bold text-[var(--pd-icon)]">{fmtPrice(price)}<span className="text-base font-medium text-[var(--pd-muted)]">/mese</span></div>
                        <div className="text-[11px] text-[var(--pd-muted)]">{state.can_start_trial ? `Primi ${state.trial_days} giorni gratis · ` : ""}IVA esclusa · pagamento sicuro con Stripe</div>
                    </div>
                    {state.can_start_trial ? (
                        <button type="button" data-testid="addon-gate-trial-btn" onClick={startTrial} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-60">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Prova gratis {state.trial_days} giorni
                        </button>
                    ) : (
                        <button type="button" data-testid="addon-gate-activate-btn" onClick={checkout} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-60">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Attiva per {fmtPrice(price)}/mese
                        </button>
                    )}
                </div>
                {error && <div data-testid="addon-gate-error" style={{ background: "var(--pd-tag-red-bg)", color: "var(--pd-tag-red)" }} className="mt-4 rounded-xl px-4 py-2.5 text-xs">{error}</div>}
            </div>
        </div>
    );
}
